import moment from "moment-timezone";

/**
 * appLifecycleTime — the pure time logic of the attendance app's
 * "auto start / auto close" feature.
 *
 * Kept free of app / Capacitor imports on purpose so it can be unit tested and so
 * it stays the single place where "when should the app close / reopen" is decided.
 * Every returned instant is an absolute epoch-millisecond timestamp — the only thing
 * the native side ever receives (it never parses a time string).
 */

/** Shape of `business_settings.attendance_app_schedule`. */
export interface AttendanceAppSchedule {
  isAutoStartStopEnabled?: boolean;
  /** "HH:mm" (business time zone) — when the app should (re)open. */
  autoStartTime?: string;
  /** "HH:mm" (business time zone) — when the app should be closed. */
  autoCloseTime?: string;
}

/**
 * Close and reopen must not land on (nearly) the same instant — that would kill
 * and relaunch the app in the same breath. 60s only rejects identical times;
 * any distinct HH:mm pair (the smallest possible gap is 1 minute) is honoured
 * exactly as configured in business settings.
 */
const MIN_TRIGGER_GAP_MS = 60 * 1000;

/**
 * Business default time zone — used ONLY when a caller passes no usable zone.
 *
 * The schedule must never depend on the DEVICE zone: the HH:mm values are configured on
 * the Settings screen and have to be read in the business zone (a provisioned attendance
 * tablet may sit in another zone, or none at all). `moment.tz.guess()` is therefore never
 * used — the same "Europe/London" default as
 * `helpers/moment.getBusinessTimeZone()` closes the chain.
 */
export const BUSINESS_FALLBACK_TIME_ZONE = "Europe/London";

export interface AppLifecycleTriggers {
  enabled: boolean;
  closeAt: number;
  reopenAt: number;
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Parses "HH:mm"; falls back to `fallback` when the value is missing or malformed. */
function parseHHmm(
  value: unknown,
  fallback: string,
): { h: number; m: number } | null {
  const raw =
    typeof value === "string" && /^\s*\d{1,2}:\d{2}\s*$/.test(value)
      ? value.trim()
      : fallback;
  const parts = raw.split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (
    !Number.isFinite(h) ||
    !Number.isFinite(m) ||
    h < 0 ||
    h > 23 ||
    m < 0 ||
    m > 59
  ) {
    return null;
  }
  return { h, m };
}

/** Whether the IANA zone name can be used with moment-timezone. */
export function isValidTimeZone(timeZone: string | null | undefined): boolean {
  return !!timeZone && !!moment.tz.zone(timeZone);
}

/**
 * Next future instant (epoch ms) when the wall clock in `timeZone` shows `hhmm`.
 * The instant is always strictly after `nowMs` (an alarm "today at 18:00" that has
 * already passed rolls over to tomorrow).
 *
 * `timeZone` is always the business zone (settings); when it is unusable the business
 * default is used instead of the DEVICE zone, so a wrongly configured tablet can never
 * shift the configured close / open moments. Returns 0 (→ schedule disarmed) when the
 * value cannot be computed at all.
 */
export function nextOccurrenceMs(
  hhmm: string,
  timeZone: string,
  nowMs: number,
): number {
  try {
    const parsed = parseHHmm(hhmm, "");
    if (!parsed) return 0;
    const zone = isValidTimeZone(timeZone)
      ? timeZone
      : BUSINESS_FALLBACK_TIME_ZONE;
    const dayKey = moment(nowMs).tz(zone).format("YYYY-MM-DD");
    let candidate = moment.tz(
      `${dayKey} ${pad(parsed.h)}:${pad(parsed.m)}`,
      "YYYY-MM-DD HH:mm",
      zone,
    );
    if (!candidate.isValid()) return 0;
    if (candidate.valueOf() <= nowMs) candidate = candidate.add(1, "day");
    return candidate.valueOf();
  } catch (error) {
    console.error("[AppLifecycle] nextOccurrenceMs failed:", error);
    return 0;
  }
}

/**
 * Turns the business schedule into the two absolute trigger instants.
 * Returns `enabled: false` when the feature is switched off or a trigger cannot
 * be computed (in which case the native schedule has to be cleared).
 *
 * @param schedule `business_settings.attendance_app_schedule`
 * @param timeZone IANA time zone the HH:mm values are expressed in (business zone)
 * @param nowMs    reference instant (defaults to the current time)
 */
export function computeAppLifecycleTriggers(
  schedule: AttendanceAppSchedule | null | undefined,
  timeZone: string,
  nowMs: number = Date.now(),
): AppLifecycleTriggers {
  const disabled: AppLifecycleTriggers = {
    enabled: false,
    closeAt: 0,
    reopenAt: 0,
  };
  if (schedule?.isAutoStartStopEnabled !== true) return disabled;

  // The times are owned by the backend (business settings) — deliberately NO
  // static front-end fallback: a missing/malformed time leaves the schedule disarmed.
  const closeTime = schedule.autoCloseTime ?? "";
  const startTime = schedule.autoStartTime ?? "";

  const closeAt = nextOccurrenceMs(closeTime, timeZone, nowMs);
  let reopenAt = nextOccurrenceMs(startTime, timeZone, nowMs);
  if (closeAt <= 0 || reopenAt <= 0) return disabled;

  // Degenerate configuration (start == close): push the reopen a full day further
  // instead of closing and reopening the app in the very same breath.
  if (Math.abs(closeAt - reopenAt) < MIN_TRIGGER_GAP_MS) {
    reopenAt = nextOccurrenceMs(startTime, timeZone, reopenAt);
    if (reopenAt <= 0) return disabled;
  }

  return { enabled: true, closeAt, reopenAt };
}
